import { inject, Injectable, signal } from '@angular/core';
import { AccountInfo } from '@groundworks/ngx-groundworks-auth';
import { firstValueFrom } from 'rxjs';
import { BranchService } from './branch.service';
import { DefaultBranchService } from './default-branch.service';
import { ProfileResponse, ProfileService } from './profile.service';
import { isInteractionRequiredError, retryTransient } from './retry-transient';

/** The post-login step that failed and left the user without their name or roles. */
export type PostLoginFailure = 'profile' | 'roles';

/**
 * Set before the automatic sign-in redirect and cleared once a Graph token is acquired (or the
 * redirect fails to start), so an interaction-required error that survives the redirect shows
 * the failure instead of redirecting in a loop.
 */
export const REAUTH_ATTEMPTED_KEY = 'home-page.post-login.reauth-attempted';

// Runs from afterInteractionComplete, which fires when the active account changes (in practice
// once per page load), and the library never retries it. Each Graph and API call therefore
// retries transient failures itself. An expired session redirects to sign in again; anything else
// still failing is logged, published through `failure` for the shell to show, and not retried
// until the next page load. Roles and branch selection each catch their own failure so both are
// reported (Promise.all would surface only the first) and run() settles only after both finish.
@Injectable({ providedIn: 'root' })
export class PostLoginService {
  private readonly profileService = inject(ProfileService);
  private readonly branchService = inject(BranchService);
  private readonly defaultBranchService = inject(DefaultBranchService);

  readonly failure = signal<PostLoginFailure | null>(null);

  async run(account: AccountInfo): Promise<void> {
    this.failure.set(null);
    let accessToken: string;
    let profile: ProfileResponse;
    try {
      // Retrying both together re-acquires the token if the first one was the failure.
      ({ accessToken, profile } = await retryTransient(async () => {
        const token = await this.profileService.getAccountAccessToken();
        // The session works again, so a later expiry in this tab may redirect again.
        clearReauthAttempt();
        return { accessToken: token, profile: await this.profileService.getProfile(token) };
      }));
    } catch (error) {
      console.error('Failed to load the Graph profile:', error);
      if (!(isInteractionRequiredError(error) && (await this.signInAgainOnce()))) {
        this.failure.set('profile');
      }
      return;
    }

    const hasIdTokenClaims = !!account.idTokenClaims;
    if (hasIdTokenClaims) {
      this.profileService.setProfile(profile);
    }
    await Promise.all([
      hasIdTokenClaims ? this.applyRoles(accessToken) : Promise.resolve(),
      this.setDefaultBranch(profile.officeLocation),
    ]);
  }

  /** Starts the sign-in redirect unless one was already attempted this session; true if started. */
  private async signInAgainOnce(): Promise<boolean> {
    if (!claimReauthAttempt()) {
      return false;
    }
    try {
      await this.profileService.signInAgain();
      return true;
    } catch (error) {
      console.error('Failed to start the sign-in redirect:', error);
      // No redirect happened, so the next page load may try again.
      clearReauthAttempt();
      return false;
    }
  }

  private async applyRoles(accessToken: string): Promise<void> {
    try {
      await retryTransient(() => this.profileService.applyRoles(accessToken));
    } catch (error) {
      console.error('Failed to load the user roles:', error);
      this.failure.set('roles');
    }
  }

  private async setDefaultBranch(locationName: string | null): Promise<void> {
    if (!locationName) {
      console.warn('No default branch set: the Graph profile has no office location');
      return;
    }
    try {
      const branches = await retryTransient(() => firstValueFrom(this.branchService.getBranches()));

      const branchDtos = branches.filter((b) => b.name === locationName);
      if (branchDtos.length === 1) {
        this.branchService.setSelectedBranch(branchDtos[0]);
        this.defaultBranchService.notifyDefaultBranchSet();
      } else {
        console.warn(`No default branch set: ${branchDtos.length} branches match office location "${locationName}"`);
      }
    } catch (error) {
      console.error('Failed to set the default branch from the office location:', error);
    }
  }
}

/** Records a sign-in redirect attempt; false if one was already made or storage is unavailable. */
function claimReauthAttempt(): boolean {
  try {
    if (sessionStorage.getItem(REAUTH_ATTEMPTED_KEY) !== null) {
      return false;
    }
    sessionStorage.setItem(REAUTH_ATTEMPTED_KEY, 'true');
    return true;
  } catch {
    return false;
  }
}

function clearReauthAttempt(): void {
  try {
    sessionStorage.removeItem(REAUTH_ATTEMPTED_KEY);
  } catch {
    // Storage is unavailable, so nothing was recorded.
  }
}
